import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "ui";

import { EAiAssistantRuleKind } from "../../aiAgents.types";
import { useGetRules, useRuleMutations } from "../../apis";
import { ChipEditor, ChipItem } from "../ChipEditor";

import styles from "./KnowledgeTab.module.scss";

interface Props {
  assistantId: string;
}

const KnowledgeTab = ({ assistantId }: Props) => {
  const { t } = useTranslation();
  const { rules, isLoading } = useGetRules(assistantId);
  const { createRule, deleteRule } = useRuleMutations(assistantId);

  const items: ChipItem[] = useMemo(
    () =>
      rules
        .filter((r) => r.kind === EAiAssistantRuleKind.Knowledge)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((r) => ({ id: r.id, label: r.body })),
    [rules]
  );

  const busy = createRule.isLoading || deleteRule.isLoading;

  const handleAdd = (text: string) =>
    createRule.mutate({
      kind: EAiAssistantRuleKind.Knowledge,
      title: text.slice(0, 200),
      body: text
    });

  const handleRemove = (ruleId: string) => deleteRule.mutate({ ruleId });

  if (isLoading) {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <ChipEditor
        variant="neutral"
        title={t("Knowledge Base")}
        subtitle={t("Facts and information the assistant can rely on")}
        placeholder={t("E.g., Delivery takes 3-5 business days")}
        items={items}
        disabled={busy}
        onAdd={handleAdd}
        onRemove={handleRemove}
      />
    </div>
  );
};

export default KnowledgeTab;
